import React, { useEffect, useState } from "react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Shield,
  Users,
  Calendar,
  Camera,
  DollarSign,
  CheckCircle2,
  XCircle,
  Trash2,
  UserPlus,
  Sparkles,
  Clock,
  Eye,
  Star,
  MessageSquare,
} from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export default function AdminPage() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [showVipForm, setShowVipForm] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [vipForm, setVipForm] = useState({
    name: "",
    email: "",
    password: "",
    social_link: "",
  });

  const loadAll = async () => {
    try {
      const [s, e, u, a, p, t] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/events"),
        api.get("/admin/users"),
        api.get("/admin/vip-applications"),
        api.get("/admin/photos"),
        api.get("/admin/testimonials"),
      ]);
      setStats(s.data);
      setEvents(e.data);
      setUsers(u.data);
      setApps(a.data);
      setPhotos(p.data);
      setTestimonials(t.data);
    } catch (err) {
      toast.error("Erro ao carregar dados do admin");
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const approvePayment = async (id) => {
    try {
      await api.post(`/admin/events/${id}/approve-payment`);
      toast.success("Pagamento aprovado!");
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const endEvent = async (id) => {
    if (!confirm("Encerrar este evento?")) return;
    await api.post(`/admin/events/${id}/end`);
    toast.success("Evento encerrado");
    loadAll();
  };

  const deleteEvent = async (id) => {
    if (!confirm("Excluir evento e todas suas fotos? Ação irreversível.")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Evento excluído");
    loadAll();
  };

  const removePhoto = async (id) => {
    if (!confirm("Remover essa foto da galeria?")) return;
    await api.delete(`/admin/photos/${id}`);
    toast.success("Foto removida");
    loadAll();
  };

  const createVip = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/vip-users", vipForm);
      toast.success("Conta VIP criada!");
      setShowVipForm(false);
      setVipForm({ name: "", email: "", password: "", social_link: "" });
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const actOnApp = async (id, action) => {
    await api.post(`/admin/vip-applications/${id}/${action}`);
    toast.success(action === "approve" ? "Aprovado" : "Rejeitado");
    loadAll();
  };

  return (
    <div className="min-h-screen text-white grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#00F0FF]" />
          </div>
          <div>
            <span className="text-xs uppercase tracking-[0.2em] text-[#00F0FF]">
              Admin
            </span>
            <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
              Painel de controle
            </h1>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-8 bg-white/5 border border-white/10">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">
              Eventos
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Usuários
            </TabsTrigger>
            <TabsTrigger value="vip" data-testid="tab-vip">
              VIP
            </TabsTrigger>
            <TabsTrigger value="moderation" data-testid="tab-moderation">
              Moderação
            </TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="tab-testimonials">
              Depoimentos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { l: "Receita", v: `R$ ${stats.revenue_brl.toFixed(2)}`, c: "#39FF14", icon: DollarSign },
                  { l: "Eventos", v: stats.total_events, c: "#FF007F", icon: Calendar },
                  { l: "Fotos", v: stats.total_photos, c: "#00F0FF", icon: Camera },
                  { l: "Usuários", v: stats.total_users, c: "#FF007F", icon: Users },
                  { l: "Aguardando PIX", v: stats.pending_payments, c: "#FFD93D", icon: Clock },
                  { l: "VIPs ativos", v: stats.vips, c: "#00F0FF", icon: Sparkles },
                  { l: "Eventos VIP", v: stats.vip_events, c: "#FF007F", icon: Sparkles },
                  { l: "Fotos VIP (alcance)", v: stats.vip_photos, c: "#39FF14", icon: Eye },
                ].map((k, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="glass rounded-2xl p-5"
                    data-testid={`stat-${k.l}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <k.icon className="w-4 h-4" style={{ color: k.c }} />
                      <span className="text-[10px] uppercase tracking-[0.15em] text-white/40">
                        {k.l}
                      </span>
                    </div>
                    <div className="font-display font-black text-2xl tracking-tight">
                      {k.v}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="events">
            <div className="space-y-3">
              {events.length === 0 ? (
                <div className="text-white/50 text-center py-8">Nenhum evento.</div>
              ) : (
                events.map((ev) => (
                  <div
                    key={ev.id}
                    className="glass rounded-2xl p-5 flex flex-wrap gap-4 items-center"
                    data-testid={`admin-event-${ev.id}`}
                  >
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display font-bold tracking-tight">{ev.name}</h3>
                        {ev.is_vip && (
                          <span className="badge-vip">
                            <Sparkles className="w-3 h-3" /> VIP
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 flex gap-3">
                        <span>
                          <Calendar className="w-3 h-3 inline" />{" "}
                          {new Date(ev.date).toLocaleDateString("pt-BR")}
                        </span>
                        <span>
                          <Camera className="w-3 h-3 inline" /> {ev.photos_count} fotos
                        </span>
                        <span>por {ev.owner_name}</span>
                      </div>
                    </div>
                    <div>
                      {ev.payment_status === "pending" ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                          Aguarda PIX
                        </span>
                      ) : ev.payment_status === "waived" ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-[#FF007F]/10 text-[#FF007F] border border-[#FF007F]/30">
                          Grátis VIP
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30">
                          Pago
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {ev.payment_status === "pending" && (
                        <button
                          onClick={() => approvePayment(ev.id)}
                          className="btn-neon text-xs px-3 py-2"
                          data-testid={`approve-${ev.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                        </button>
                      )}
                      {ev.status !== "ended" && (
                        <button
                          onClick={() => endEvent(ev.id)}
                          className="btn-cyan text-xs px-3 py-2"
                          data-testid={`end-${ev.id}`}
                        >
                          Encerrar
                        </button>
                      )}
                      <button
                        onClick={() => deleteEvent(ev.id)}
                        className="text-xs px-3 py-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
                        data-testid={`delete-${ev.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="users">
            <div className="glass rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs uppercase tracking-[0.15em] text-white/50">
                  <tr>
                    <th className="text-left p-4">Nome</th>
                    <th className="text-left p-4">Email</th>
                    <th className="text-left p-4">Role</th>
                    <th className="text-left p-4">Cadastro</th>
                    <th className="text-right p-4">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-white/5" data-testid={`user-${u.id}`}>
                      <td className="p-4">{u.name}</td>
                      <td className="p-4 text-white/70">{u.email}</td>
                      <td className="p-4">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            u.role === "admin"
                              ? "bg-[#00F0FF]/10 text-[#00F0FF]"
                              : u.role === "vip"
                              ? "bg-[#FF007F]/10 text-[#FF007F]"
                              : "bg-white/10 text-white/70"
                          }`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td className="p-4 text-white/50 text-xs">
                        {new Date(u.created_at).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="p-4 text-right">
                        {u.role !== "admin" && (
                          <button
                            onClick={async () => {
                              if (
                                !confirm(
                                  `Excluir ${u.name} (${u.email})?\nTodos os eventos, fotos, reações e depoimentos deste usuário serão apagados. Ação irreversível.`,
                                )
                              )
                                return;
                              try {
                                const { data } = await api.delete(
                                  `/admin/users/${u.id}`,
                                );
                                toast.success(
                                  `Usuário excluído${data.deleted_events ? ` (${data.deleted_events} evento${data.deleted_events === 1 ? "" : "s"} removido${data.deleted_events === 1 ? "" : "s"})` : ""}`,
                                );
                                loadAll();
                              } catch (err) {
                                toast.error(
                                  formatApiErrorDetail(err.response?.data?.detail),
                                );
                              }
                            }}
                            className="text-xs px-3 py-1.5 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors inline-flex items-center gap-1"
                            data-testid={`delete-user-${u.id}`}
                            title="Excluir usuário"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Excluir
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="vip">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg tracking-tight">
                Solicitações VIP
              </h3>
              <button
                onClick={() => setShowVipForm(true)}
                className="btn-neon text-sm"
                data-testid="create-vip-btn"
              >
                <UserPlus className="w-4 h-4" /> Criar conta VIP
              </button>
            </div>

            {showVipForm && (
              <form
                onSubmit={createVip}
                className="glass rounded-2xl p-6 mb-6 grid md:grid-cols-2 gap-4"
                data-testid="vip-create-form"
              >
                <input
                  required
                  placeholder="Nome"
                  value={vipForm.name}
                  onChange={(e) => setVipForm({ ...vipForm, name: e.target.value })}
                  className="input-dark"
                />
                <input
                  required
                  type="email"
                  placeholder="Email"
                  value={vipForm.email}
                  onChange={(e) => setVipForm({ ...vipForm, email: e.target.value })}
                  className="input-dark"
                />
                <input
                  required
                  type="password"
                  placeholder="Senha temporária"
                  value={vipForm.password}
                  onChange={(e) => setVipForm({ ...vipForm, password: e.target.value })}
                  className="input-dark"
                />
                <input
                  placeholder="Link Instagram (opcional)"
                  value={vipForm.social_link}
                  onChange={(e) =>
                    setVipForm({ ...vipForm, social_link: e.target.value })
                  }
                  className="input-dark"
                />
                <div className="md:col-span-2 flex gap-3">
                  <button type="submit" className="btn-neon" data-testid="save-vip">
                    Salvar
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowVipForm(false)}
                    className="btn-cyan"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            <div className="space-y-3">
              {apps.length === 0 ? (
                <div className="text-white/50 text-center py-8">
                  Nenhuma solicitação ainda.
                </div>
              ) : (
                apps.map((a) => (
                  <div
                    key={a.id}
                    className="glass rounded-2xl p-5 flex flex-wrap gap-4 items-center"
                    data-testid={`vip-app-${a.id}`}
                  >
                    <div className="flex-1 min-w-[220px]">
                      <h4 className="font-bold">{a.name}</h4>
                      <div className="text-xs text-white/60">{a.email}</div>
                      <a
                        href={a.social_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[#00F0FF] hover:underline"
                      >
                        {a.social_link}
                      </a>
                      {a.followers && (
                        <div className="text-xs text-white/50 mt-1">
                          Seguidores: {a.followers}
                        </div>
                      )}
                      {a.message && (
                        <p className="text-sm text-white/70 mt-2">{a.message}</p>
                      )}
                    </div>
                    <div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          a.status === "pending"
                            ? "bg-yellow-500/10 text-yellow-400"
                            : a.status === "approved"
                            ? "bg-[#39FF14]/10 text-[#39FF14]"
                            : "bg-red-500/10 text-red-400"
                        }`}
                      >
                        {a.status}
                      </span>
                    </div>
                    {a.status === "pending" && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => actOnApp(a.id, "approve")}
                          className="btn-neon text-xs px-3 py-2"
                          data-testid={`app-approve-${a.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                        </button>
                        <button
                          onClick={() => actOnApp(a.id, "reject")}
                          className="btn-cyan text-xs px-3 py-2"
                          data-testid={`app-reject-${a.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" /> Rejeitar
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="moderation">
            {photos.length === 0 ? (
              <div className="text-white/50 text-center py-8">
                Nenhuma foto ainda.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {photos.map((p) => (
                  <div
                    key={p.id}
                    className="relative group rounded-lg overflow-hidden aspect-square"
                    data-testid={`mod-photo-${p.id}`}
                  >
                    <img
                      src={fileUrl(p.storage_path)}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center p-2 text-center">
                      <div className="text-xs text-white truncate max-w-full">
                        {p.guest_name}
                      </div>
                      <button
                        onClick={() => removePhoto(p.id)}
                        className="mt-2 text-xs px-3 py-1.5 rounded-full bg-red-500 text-white flex items-center gap-1"
                        data-testid={`remove-photo-${p.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Remover
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="testimonials">
            {testimonials.length === 0 ? (
              <div className="text-white/50 text-center py-8">
                Nenhum depoimento ainda. Depoimentos aparecem aqui depois que o
                anfitrião envia (após evento encerrado).
              </div>
            ) : (
              <div className="space-y-3">
                {testimonials.map((t) => (
                  <div
                    key={t.id}
                    className="glass rounded-2xl p-5 flex flex-wrap gap-4 items-start"
                    data-testid={`testimonial-${t.id}`}
                  >
                    <div className="flex-1 min-w-[240px]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < t.rating
                                  ? "fill-[#FFD700] text-[#FFD700]"
                                  : "text-white/20"
                              }`}
                            />
                          ))}
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            t.status === "pending"
                              ? "bg-yellow-500/10 text-yellow-400"
                              : t.status === "approved"
                              ? "bg-[#39FF14]/10 text-[#39FF14]"
                              : "bg-red-500/10 text-red-400"
                          }`}
                        >
                          {t.status}
                        </span>
                      </div>
                      <p className="text-sm text-white/90 leading-relaxed">
                        “{t.message}”
                      </p>
                      <div className="text-xs text-white/50 mt-2">
                        <span className="font-bold text-white/80">
                          {t.author_name}
                        </span>
                        {" · "}
                        {t.event_name}
                        {" · "}
                        {new Date(t.created_at).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {t.status !== "approved" && (
                        <button
                          onClick={async () => {
                            await api.post(
                              `/admin/testimonials/${t.id}/approve`,
                            );
                            toast.success("Aprovado");
                            loadAll();
                          }}
                          className="btn-neon text-xs px-3 py-2"
                          data-testid={`approve-testimonial-${t.id}`}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Aprovar
                        </button>
                      )}
                      {t.status !== "rejected" && (
                        <button
                          onClick={async () => {
                            await api.post(
                              `/admin/testimonials/${t.id}/reject`,
                            );
                            toast.success("Rejeitado");
                            loadAll();
                          }}
                          className="btn-cyan text-xs px-3 py-2"
                          data-testid={`reject-testimonial-${t.id}`}
                        >
                          <XCircle className="w-3.5 h-3.5" /> Rejeitar
                        </button>
                      )}
                      <button
                        onClick={async () => {
                          if (!confirm("Excluir depoimento?")) return;
                          await api.delete(`/admin/testimonials/${t.id}`);
                          toast.success("Excluído");
                          loadAll();
                        }}
                        className="text-xs px-3 py-2 rounded-full border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors"
                        data-testid={`delete-testimonial-${t.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
